import React from 'react'
import { useNavigate } from 'react-router-dom'
import {  ChevronDown,Check, BarChart3 } from 'lucide-react'
import { useSyncedStore } from '@syncedstore/react'
import { todoStore, addWorkerAssignmentLog, softDeleteWorkerAssignmentLog } from 'src/store/todo'
import { useParcelStore } from 'src/store/parcel'
import Loader from 'src/components/shared/loader'
import Fallback from 'src/components/shared/fallback'
import Container from 'src/components/shared/container'
import PageHeader from 'src/components/shared/page-header'
import RecordValueModal from 'src/components/shared/record-value-modal'
import WorkerValueSummaryModal from 'src/components/shared/worker-value-summary-modal'

type Zone = {
  id: string
  zone_moniker: string
  widget_zone_row?: number
  widget_zone_column?: number
}
 
type TaskType = {
  id: string
  task_type: string
  task_type_description: string
  task_type_moniker?: string
}

type Worker = {
  id: string
  first_name: string
  last_name: string
  email: string
}

const STORAGE_KEY = 'activities_selected_team'

type ZoneDetailsView = {
  workerId: string
  taskTypeId: string
  zoneId: string
  zoneName: string
} | null

type RecordModalState = {
  isOpen: boolean
  assignmentId: string
  workerId: string
  taskTypeId: string
  zoneId: string
  locationId?: string
} | null

type SummaryModalState = {
  isOpen: boolean
  workerId: string
  workerName: string
  taskTypeId: string
  taskName: string
} | null

type ViewMode = 'grid' | 'analytics'

const AnalyticsPage: React.FC = () => {
  const navigate = useNavigate()

  console.log({navigate})

  const [selectedTeamId, setSelectedTeamId] = React.useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEY)
  })
  const [showTeamDropdown, setShowTeamDropdown] = React.useState(false)
  const [selectedTasks, setSelectedTasks] = React.useState<string[]>([])
  const [zoneDetailsView, setZoneDetailsView] = React.useState<ZoneDetailsView>(null)
  const [recordModalState, setRecordModalState] = React.useState<RecordModalState>(null)
  const [summaryModalState, setSummaryModalState] = React.useState<SummaryModalState>(null)
  const [deleteConfirmLogId, setDeleteConfirmLogId] = React.useState<string | null>(null)
  const [viewMode, setViewMode] = React.useState<ViewMode>('grid')
  const { parcel: parcelId } = useParcelStore()

  const state = useSyncedStore(todoStore)
  const teams = state.teams || []

  // Initialize all tasks as selected on first load
  const tasksInitialized = React.useRef(false)

  // Get data from Yjs and map to GraphQL format for compatibility
  const workers = state.workers?.filter(w => w.teamId === selectedTeamId && w.isActive) || []
  const zones = state.zones?.filter(z => z.parcelId === parcelId && z.isActive) || []
  const tasks = React.useMemo(() =>
    (state.taskTypes || []).map(t => ({
      id: t.id,
      task_type: t.taskType,
      task_type_description: t.taskTypeDescription,
      task_type_moniker: t.taskTypeMoniker
    })), [state.taskTypes]
  )

  // Better color palette for task types - distinct and visible
  const taskColorPalette = ['#1B6EFF', '#39A64D', '#8667FF', '#00A5A7', '#FF9F1C']

  // Get task color based on task type ID
  const getTaskColor = (taskTypeId: string): string => {
    const taskIndex = tasks.findIndex((t) => t.id === taskTypeId)
    if (taskIndex === -1) return '#A3A3A8' // default gray
    return taskColorPalette[taskIndex % taskColorPalette.length]
  }

  // Initialize all tasks as selected on component mount
  React.useEffect(() => {
    if (!tasksInitialized.current && tasks.length > 0) {
      setSelectedTasks(tasks.map((t) => t.id))
      tasksInitialized.current = true
    }
  }, [tasks])

  // Group workers by zone from assignments - one entry per worker+task combination per zone
  // Note: Intentionally NOT memoized to ensure reactive updates from synced store
  const workersByZone: Record<string, Array<{ workerId: string; taskTypeId: string; locationCount: number; completedLocationCount: number; hasZoneLevelAssignment: boolean }>> = {}

  state.workerAssignments.forEach((assignment) => {
    if (
      assignment.teamId === selectedTeamId &&
      !assignment.softDeleted &&
      (!selectedTasks.length || selectedTasks.includes(assignment.taskTypeId))
    ) {
      if (!workersByZone[assignment.zoneId]) {
        workersByZone[assignment.zoneId] = []
      }
      
      // Check if this worker+task combination already exists for this zone
      const existingEntry = workersByZone[assignment.zoneId].find(
        e => e.workerId === assignment.workerId && e.taskTypeId === assignment.taskTypeId
      )
      
      if (existingEntry) {
        // Increment location count and completed count if applicable
        if (assignment.locationId) {
          existingEntry.locationCount += 1
          if (assignment.completed) {
            existingEntry.completedLocationCount += 1
          }
        } else {
          // Mark if there's a zone-level assignment (no locationId)
          existingEntry.hasZoneLevelAssignment = true
        }
      } else {
        // New entry for this worker+task in this zone
        workersByZone[assignment.zoneId].push({
          workerId: assignment.workerId,
          taskTypeId: assignment.taskTypeId,
          locationCount: assignment.locationId ? 1 : 0,
          completedLocationCount: (assignment.locationId && assignment.completed) ? 1 : 0,
          hasZoneLevelAssignment: !assignment.locationId,
        })
      }
    }
  })

  // Build worker map for quick lookup
  const workersMap = React.useMemo(() => {
    const map: Record<string, Worker> = {}
    workers.forEach((w) => {
      map[w.id] = {
        id: w.id,
        first_name: w.firstName,
        last_name: w.lastName,
        email: w.email
      }
    })
    return map
  }, [workers])

  // Build a proper grid: rows x columns with contiguous positions
  const buildGridLayout = React.useMemo(() => {
    
    // Find the dimensions
    let maxRow = 0
    let maxColumn = 0
    const zoneMap: Record<string, Zone> = {}

    zones.forEach((zone) => {
      const row = zone.widgetZoneRow ?? 0
      const col = zone.widgetZoneColumn ?? 0
      maxRow = Math.max(maxRow, row)
      maxColumn = Math.max(maxColumn, col)
      zoneMap[`${col}-${row}`] = {
        id: zone.id,
        zone_moniker: zone.zoneMoniker,
        widget_zone_row: zone.widgetZoneRow,
        widget_zone_column: zone.widgetZoneColumn
      } as any
    })

    // Create grid array: grid[column][row] = zone
    const fullGrid: Array<Array<Zone | null>> = []
    for (let col = 0; col <= maxColumn; col++) {
      fullGrid[col] = []
      for (let row = 0; row <= maxRow; row++) {
        fullGrid[col][row] = zoneMap[`${col}-${row}`] || null
      }
    }

    // Filter out completely empty columns
    const filteredGrid = fullGrid.filter(column => 
      column.some(zone => zone !== null)
    )

    // Find which rows have at least one zone
    const rowsWithZones = new Set<number>()
    zones.forEach((zone) => {
      const row = zone.widgetZoneRow ?? 0
      rowsWithZones.add(row)
    })
    const sortedRows = Array.from(rowsWithZones).sort((a, b) => a - b)

    return { grid: filteredGrid, rowIndices: sortedRows }
  }, [zones])

  const { grid, rowIndices } = buildGridLayout

  // Build tasks map for quick lookup (move outside conditional to avoid hook order issues)
  const tasksMap = React.useMemo(() => {
    const map: Record<string, TaskType> = {}
    tasks.forEach((t) => {
      map[t.id] = t
    })
    return map
  }, [tasks])

  // Check if a task is a value-type task (e.g., HVST)
  const isValueTypeTask = (taskTypeId: string): boolean => {
    const task = tasksMap[taskTypeId]
    return task?.task_type_moniker === 'HVST'
  }

  // Get logs for an assignment
  const getAssignmentLogs = (workerId: string, taskTypeId: string, zoneId: string, locationId?: string) => {
    return state.workerAssignmentLogs.filter(
      log =>
        log.workerId === workerId &&
        log.taskTypeId === taskTypeId &&
        log.zoneId === zoneId &&
        log.locationId === locationId
    )
  }

  // Get total value for an assignment across all logs (active ones only)
  const getAssignmentTotalValue = (workerId: string, taskTypeId: string, zoneId: string, locationId?: string) => {
    return getAssignmentLogs(workerId, taskTypeId, zoneId, locationId)
      .filter(log => !log.softDeleted)
      .reduce((sum, log) => sum + log.value, 0)
  }

  // Get total value for an assignment at zone level (sum of all location-level logs for HVST tasks)
  const getAssignmentZoneTotalValue = (workerId: string, taskTypeId: string, zoneId: string) => {
    return state.workerAssignmentLogs.filter(
      log =>
        log.workerId === workerId &&
        log.taskTypeId === taskTypeId &&
        log.zoneId === zoneId &&
        log.locationId && // Only include location-level logs
        !log.softDeleted
    ).reduce((sum, log) => sum + log.value, 0)
  }

  // Get zone-level total (logs without locationId for this worker+task+zone)
  const getZoneLevelTotal = (workerId: string, taskTypeId: string, zoneId: string) => {
    return state.workerAssignmentLogs.filter(
      log =>
        log.workerId === workerId &&
        log.taskTypeId === taskTypeId &&
        log.zoneId === zoneId &&
        !log.locationId && // Only zone-level logs (no location)
        !log.softDeleted
    ).reduce((sum, log) => sum + log.value, 0)
  }

  // Handle opening record modal
  const handleOpenRecordModal = (workerId: string, taskTypeId: string, zoneId: string, locationId?: string) => {
    setRecordModalState({
      isOpen: true,
      assignmentId: `${workerId}-${taskTypeId}-${zoneId}-${locationId || ''}`,
      workerId,
      taskTypeId,
      zoneId,
      locationId
    })
  }

  // Handle saving a recorded value
  const handleSaveRecordedValue = (value: number) => {
    if (recordModalState && selectedTeamId) {
      try {
        addWorkerAssignmentLog(
          selectedTeamId,
          recordModalState.workerId,
          recordModalState.taskTypeId,
          recordModalState.zoneId,
          value,
          recordModalState.locationId
        )
        // Auto-mark as completed for value-type tasks
        const assignment = state.workerAssignments.find(
          a => a.workerId === recordModalState.workerId &&
               a.taskTypeId === recordModalState.taskTypeId &&
               a.zoneId === recordModalState.zoneId &&
               a.locationId === recordModalState.locationId &&
               !a.softDeleted
        )
        if (assignment && !assignment.completed) {
          assignment.completed = true
        }
        setRecordModalState(null)
      } catch (error) {
        console.error('Error saving recorded value:', error)
      }
    }
  }

  // Handle deleting a recorded value
  const handleDeleteRecordedValue = (logId: string) => {
    try {
      softDeleteWorkerAssignmentLog(logId)
    } catch (error) {
      console.error('Error deleting recorded value:', error)
    }
  }

  // Format timestamp to readable format
  const formatTime = (timestamp: number): string => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  // Format number without trailing zeros
  const formatNumber = (num: number): string => {
    return num.toFixed(2).replace(/\.?0+$/, '')
  }

  // Get summary data for a worker across all zones and locations - returns all individual records
  const getWorkerValueSummary = (workerId: string, taskTypeId: string) => {
    const logs = state.workerAssignmentLogs.filter(
      log =>
        log.workerId === workerId &&
        log.taskTypeId === taskTypeId &&
        !log.softDeleted
    )

    // Sort by timestamp descending (most recent first)
    const sortedLogs = [...logs].sort((a, b) => b.timestamp - a.timestamp)

    // Convert to array format with zone and location names
    const summaryData: Array<{
      id: string
      zoneId: string
      zoneName: string
      locationId?: string
      locationName?: string
      value: number
      timestamp: number
      formattedTime: string
    }> = []

    const zoneMap: Record<string, any> = {}
    zones.forEach((z) => {
      zoneMap[z.id] = { zone_moniker: z.zoneMoniker }
    })

    sortedLogs.forEach(log => {
      const zone = zoneMap[log.zoneId]
      const zoneName = zone?.zone_moniker || log.zoneId
      const location = state.locations.find(l => l.id === log.locationId)
      const locationName = location?.locationMoniker || location?.locationName

      summaryData.push({
        id: log.id,
        zoneId: log.zoneId,
        zoneName,
        locationId: log.locationId,
        locationName,
        value: log.value,
        timestamp: log.timestamp,
        formattedTime: formatTime(log.timestamp)
      })
    })

    return summaryData
  }

  // Open summary modal
  const handleOpenSummaryModal = (workerId: string, workerName: string, taskTypeId: string, taskName: string) => {
    setSummaryModalState({
      isOpen: true,
      workerId,
      workerName,
      taskTypeId,
      taskName
    })
  }

  const toggleTask = (taskId: string) => {
    setSelectedTasks((prev) =>
      prev.includes(taskId)
        ? prev.filter((t) => t !== taskId)
        : [...prev, taskId]
    )
  }

  // Development testing: Remove all worker assignments (actually remove, not soft delete)
  const handleClearAllAssignments = () => {
    if (confirm('Are you sure you want to remove ALL worker assignments? This is for development testing.')) {
      // Get all assignments for this team
      const assignmentsToRemove = state.workerAssignments.filter(a => a.teamId === selectedTeamId)
      
      // Actually remove them from the synced store (not soft delete)
      assignmentsToRemove.forEach((assignment) => {
        const index = state.workerAssignments.indexOf(assignment)
        if (index > -1) {
          state.workerAssignments.splice(index, 1)
        }
      })
    }
  }

  console.log({handleClearAllAssignments})

  const handleTeamSelect = (teamId: string) => {
    setSelectedTeamId(teamId)
    localStorage.setItem(STORAGE_KEY, teamId)
    setShowTeamDropdown(false)
  }

  const selectedTeam = teams.find((t) => t.id === selectedTeamId)

  // Get all locations for a worker's assignment in a zone
  const getLocationsForAssignment = (workerId: string, taskTypeId: string, zoneId: string) => {
    return state.workerAssignments.filter(
      a => a.workerId === workerId && a.taskTypeId === taskTypeId && a.zoneId === zoneId && !a.softDeleted
    )
  }

  console.log({getLocationsForAssignment})

  // Get all unique assignments for a zone
  const getAllZoneAssignments = (zoneId: string) => {
    const assignments = state.workerAssignments.filter(
      a => a.zoneId === zoneId && a.teamId === selectedTeamId && !a.softDeleted
    )
    return assignments
  }

  // If viewing zone details, show the details page
  if (zoneDetailsView) {
    const zone = zones.find((z) => z.id === zoneDetailsView.zoneId)
    const zoneAssignments = getAllZoneAssignments(zoneDetailsView.zoneId)

    return (
      <div className="min-h-screen w-full flex flex-col bg-gray-50">
        <PageHeader
          title={`${zone?.zoneMoniker} - Assignments`}
          isDetail
          onBack={() => setZoneDetailsView(null)}
        />

        <div className="flex-1 overflow-y-auto">
          <Container>
            <div className="flex flex-col gap-4 py-6 pb-32">
              {zoneAssignments.length > 0 ? (
                <div className="space-y-3">
                  {zoneAssignments.map((assignment, idx) => {
                    const worker = workersMap[assignment.workerId]
                    const task = tasksMap[assignment.taskTypeId]
                    const taskColor = getTaskColor(assignment.taskTypeId)
                    const isValueType = isValueTypeTask(assignment.taskTypeId)
                    const logs = getAssignmentLogs(assignment.workerId, assignment.taskTypeId, assignment.zoneId, assignment.locationId)
                    const totalValue = getAssignmentTotalValue(assignment.workerId, assignment.taskTypeId, assignment.zoneId, assignment.locationId)

                    if (!worker || !task) return null

                    return (
                      <div
                        key={idx}
                        className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow flex flex-col gap-3"
                      >
                        <div className="flex items-stretch gap-3">
                          <div className="flex flex-col items-center gap-2">
                            <div
                              className="w-10 h-10 rounded-md flex items-center justify-center text-xs font-bold text-white"
                              style={{ backgroundColor: taskColor }}
                            >
                              {`${worker.first_name?.[0]}${worker.last_name?.[0]}`.toUpperCase()}
                            </div>
                            {isValueType && (
                              <button
                                onClick={() => handleOpenSummaryModal(
                                  assignment.workerId,
                                  `${worker.first_name} ${worker.last_name}`,
                                  assignment.taskTypeId,
                                  task.task_type
                                )}
                                className="w-6 h-6 rounded-md flex items-center justify-center text-sm bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors flex-shrink-0"
                                title="Show all records"
                              >
                                ℹ
                              </button>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900">
                              {worker.first_name} {worker.last_name}
                            </h3>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
                              <span className="px-2 py-1 rounded-full text-white" style={{ backgroundColor: taskColor }}>
                                {task.task_type}
                              </span>
                              <span className="text-gray-600">{zone?.zoneMoniker}</span>
                              {assignment.locationId && (
                                <>
                                  <span className="text-gray-600">:</span>
                                  <span className="text-gray-600">
                                    {(() => {
                                      const location = state.locations.find(l => l.id === assignment.locationId)
                                      return location?.locationMoniker || assignment.locationId
                                    })()}
                                  </span>
                                </>
                              )}
                            </div>
                            {isValueType && totalValue > 0 && (
                              <p className="text-xs text-gray-600 mt-1">Total: {formatNumber(totalValue)}</p>
                            )}
                          </div>
                          {isValueType ? (
                            <button
                              onClick={() => handleOpenRecordModal(assignment.workerId, assignment.taskTypeId, assignment.zoneId, assignment.locationId)}
                              className="flex-shrink-0 px-4 py-2 font-medium rounded-lg transition-colors bg-purple-100 text-purple-700 hover:bg-purple-200"
                            >
                              Record
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                const assignmentIndex = state.workerAssignments.findIndex(
                                  a => a.id === assignment.id
                                )
                                if (assignmentIndex !== -1) {
                                  state.workerAssignments[assignmentIndex].completed = 
                                    !state.workerAssignments[assignmentIndex].completed
                                }
                              }}
                              className={`flex-shrink-0 px-4 py-2 font-medium rounded-lg transition-colors flex items-center gap-2 h-full ${
                                assignment.completed
                                  ? 'bg-green-600 text-white hover:bg-green-700'
                                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                              }`}
                            >
                              {assignment.completed ? (
                                <>
                                  <Check size={18} />
                                  Done
                                </>
                              ) : (
                                'Done?'
                              )}
                            </button>
                          )}
                        </div>

                        {/* Show logs for value-type tasks */}
                        {isValueType && logs.length > 0 && (
                          <div className="border-t border-gray-200 pt-3 mt-3">
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                              {logs.map((log) => (
                                <div
                                  key={log.id}
                                  className={`flex items-center justify-between text-sm p-2 bg-gray-50 rounded ${
                                    log.softDeleted ? 'bg-red-50' : ''
                                  }`}
                                >
                                  <div className="flex-1">
                                    <span className={`font-semibold ${log.softDeleted ? 'line-through text-gray-500' : 'text-gray-900'}`}>{formatNumber(log.value)}</span>
                                    <span className={`ml-1 ${log.softDeleted ? 'line-through text-gray-400' : 'text-gray-600'}`}>@ {formatTime(log.timestamp)}</span>
                                    {log.softDeleted && log.softDeletedTime && (
                                      <span className="text-red-600 ml-2">(deleted {formatTime(log.softDeletedTime)})</span>
                                    )}
                                  </div>
                                  {!log.softDeleted && (
                                    <button
                                      onClick={() => setDeleteConfirmLogId(log.id)}
                                      className="ml-2 w-6 h-6 flex items-center justify-center rounded hover:bg-red-200 transition-colors text-red-600 hover:text-red-700 flex-shrink-0 text-lg font-bold"
                                      title="Delete record"
                                    >
                                      ✕
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <Fallback title="No assignments found for this zone" />
              )}
            </div>
          </Container>
        </div>

        {/* Record Value Modal */}
        {recordModalState && (
          <RecordValueModal
            isOpen={recordModalState.isOpen}
            title="# of Boxes"
            onSave={handleSaveRecordedValue}
            onCancel={() => setRecordModalState(null)}
          />
        )}

        {/* Summary Modal */}
        {summaryModalState && (
          <WorkerValueSummaryModal
            isOpen={summaryModalState.isOpen}
            workerName={summaryModalState.workerName}
            taskName={summaryModalState.taskName}
            summaryData={getWorkerValueSummary(summaryModalState.workerId, summaryModalState.taskTypeId)}
            onClose={() => setSummaryModalState(null)}
          />
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmLogId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-xs mx-4">
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Record?</h3>
                <p className="text-sm text-gray-600 mb-6">This record will be marked as deleted but kept for audit purposes.</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setDeleteConfirmLogId(null)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleDeleteRecordedValue(deleteConfirmLogId)
                      setDeleteConfirmLogId(null)
                    }}
                    className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  if (!selectedTeamId) {
    return (
      <div className="min-h-screen w-full flex flex-col bg-gray-50">
        <PageHeader title="Activities" />
        
        <div className="flex-1 overflow-y-auto">
          <Container>
            <div className="flex flex-col gap-6 py-6">
              <div>
                <h2 className="text-lg font-semibold mb-4">Select a Team</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {teams.length > 0 ? (
                    teams.map((team) => (
                      <button
                        key={team.id}
                        onClick={() => handleTeamSelect(team.id)}
                        className="p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
                      >
                        <h3 className="font-semibold text-gray-900">{team.teamName}</h3>
                        <p className="text-sm text-gray-600 mt-1">{team.teamDescription}</p>
                      </button>
                    ))
                  ) : (
                    <Fallback title="No teams found" />
                  )}
                </div>
              </div>
            </div>
          </Container>
        </div>
      </div>
    )
  }

  if (zones.length === 0 || tasks.length === 0) return <Loader />
  if (zones.length === 0) return <Fallback title="No zones found" />

  return (
    <div className="min-h-screen w-full flex flex-col bg-gray-50">
      <PageHeader
        title={selectedTeam?.teamName || 'Activities'}
        rightContent={
          <div className="flex items-center gap-3">
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'analytics' : 'grid')}
              className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                viewMode === 'analytics'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
              title="Toggle Analytics View"
            >
              <BarChart3 size={16} />
            </button>
            <div className="relative">
              <button
                onClick={() => setShowTeamDropdown(!showTeamDropdown)}
                className="px-3 py-2 text-sm font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                Switch Team
                <ChevronDown size={16} />
              </button>
              
              {showTeamDropdown && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                  {teams.map((team) => (
                    <button
                      key={team.id}
                      onClick={() => handleTeamSelect(team.id)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors ${
                        selectedTeamId === team.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="font-semibold text-gray-900">{team.teamName}</div>
                      <div className="text-xs text-gray-600">{team.teamDescription}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto">
        <Container>
          {viewMode === 'analytics' ? (
            // Analytics View
            <div className="flex flex-col gap-3 py-4 pb-32">
              {/* Task Summary */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Task</h3>
                <div className="space-y-0.5">
                  {tasks.map((task: TaskType) => {
                    const isValueTypeTask = task.task_type_moniker === 'HVST'
                    const taskAssignments = state.workerAssignments.filter(
                      a => a.taskTypeId === task.id && a.teamId === selectedTeamId && !a.softDeleted
                    )

                    if (isValueTypeTask) {
                      // For HVST tasks, show only total box count
                      const totalBoxes = state.workerAssignmentLogs.filter(
                        log =>
                          log.taskTypeId === task.id &&
                          log.teamId === selectedTeamId &&
                          !log.softDeleted
                      ).reduce((sum, log) => sum + log.value, 0)

                      if (totalBoxes === 0 && taskAssignments.length === 0) return null

                      return (
                        <div key={task.id} className="p-1 bg-gray-50 rounded" style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center' }}>
                          <div>
                            <p className="text-sm text-gray-900">{task.task_type}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-900">{formatNumber(totalBoxes)} boxes</p>
                          </div>
                        </div>
                      )
                    } else {
                      // For non-HVST tasks, show count and percentage
                      const completedCount = taskAssignments.filter(a => a.completed).length
                      const totalCount = taskAssignments.length
                      const percentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

                      if (totalCount === 0) return null

                      return (
                        <div key={task.id} className="p-1 bg-gray-50 rounded" style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center' }}>
                          <div>
                            <p className="text-sm text-gray-900">{task.task_type}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-900">{completedCount}/{totalCount} ({percentage}%)</p>
                          </div>
                        </div>
                      )
                    }
                  })}

                  {/* Single Total line at bottom */}
                  {(() => {
                    const hvstTasks = tasks.filter((t: TaskType) => t.task_type_moniker === 'HVST')
                    const nonHvstTasks = tasks.filter((t: TaskType) => t.task_type_moniker !== 'HVST')

                    const totalTeamBoxes = state.workerAssignmentLogs.filter(
                      (log: any) =>
                        hvstTasks.some((t: TaskType) => t.id === log.taskTypeId) &&
                        log.teamId === selectedTeamId &&
                        !log.softDeleted
                    ).reduce((sum: number, log: any) => sum + log.value, 0)

                    const allNonHvstAssignments = state.workerAssignments.filter(
                      (a: any) =>
                        nonHvstTasks.some((t: TaskType) => t.id === a.taskTypeId) &&
                        a.teamId === selectedTeamId &&
                        !a.softDeleted
                    )
                    const nonHvstCompleted = allNonHvstAssignments.filter(a => a.completed).length
                    const nonHvstTotal = allNonHvstAssignments.length
                    const nonHvstPercentage = nonHvstTotal > 0 ? Math.round((nonHvstCompleted / nonHvstTotal) * 100) : 0

                    // Only show total row when there is something to show
                    if ((hvstTasks.length === 0 || totalTeamBoxes === 0) && nonHvstTotal === 0) return null

                    return (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <div className="p-1 bg-white rounded" style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center' }}>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">Total</p>
                          </div>
                          <div className="text-right text-sm">
                            {hvstTasks.length > 0 && totalTeamBoxes > 0 && (
                              <div className="text-blue-600 font-bold">{formatNumber(totalTeamBoxes)} boxes</div>
                            )}
                            {nonHvstTasks.length > 0 && nonHvstTotal > 0 && (
                              <div className="text-green-600 font-bold">{nonHvstCompleted}/{nonHvstTotal} ({nonHvstPercentage}%)</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })()}
                </div>
              </div>

              {/* Worker Summary */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Workers</h3>
                <div className="space-y-1">
                  {(() => {
                    const allWorkers = workers || []
                    
                    // Calculate team total for percentage
                    const hvstTasks = tasks.filter((t: TaskType) => t.task_type_moniker === 'HVST')
                    const teamTotalBoxes = state.workerAssignmentLogs.filter(
                      (log: any) => 
                        hvstTasks.some((t: TaskType) => t.id === log.taskTypeId) &&
                        log.teamId === selectedTeamId &&
                        !log.softDeleted
                    ).reduce((sum: number, log: any) => sum + log.value, 0)
                    
                    // Process each worker and calculate their totals
                    const workersWithData = allWorkers.map((worker) => {
                      const workerAssignments = state.workerAssignments.filter(
                        a => a.workerId === worker.id && a.teamId === selectedTeamId && !a.softDeleted
                      )
                      const totalCount = workerAssignments.length

                      if (totalCount === 0) return null

                      // Group by task type
                      const taskGroups: Record<string, any> = {}
                      workerAssignments.forEach(assignment => {
                        if (!taskGroups[assignment.taskTypeId]) {
                          taskGroups[assignment.taskTypeId] = { total: 0, completed: 0, task: tasksMap[assignment.taskTypeId] }
                        }
                        taskGroups[assignment.taskTypeId].total += 1
                        if (assignment.completed) taskGroups[assignment.taskTypeId].completed += 1
                      })

                      // Calculate box totals for HVST tasks
                      const totalHVSTBoxes = state.workerAssignmentLogs.filter(
                        log =>
                          log.workerId === worker.id &&
                          Object.keys(taskGroups).some(taskId => taskGroups[taskId].task?.task_type_moniker === 'HVST' && log.taskTypeId === taskId) &&
                          log.teamId === selectedTeamId &&
                          !log.softDeleted
                      ).reduce((sum, log) => sum + log.value, 0)

                      // Check if worker has only HVST tasks
                      const hasHVSTOnly = Object.values(taskGroups).every(g => g.task?.task_type_moniker === 'HVST')

                      return {
                        worker,
                        taskGroups,
                        hasHVSTOnly,
                        totalHVSTBoxes
                      }
                    }).filter(Boolean)

                    // Sort: HVST workers first (by total boxes descending), then others
                    type WorkerData = {
                      worker: { id: string; firstName: string; lastName: string; email: string }
                      taskGroups: Record<string, { total: number; completed: number; task?: TaskType }>
                      hasHVSTOnly: boolean
                      totalHVSTBoxes: number
                    }

                    const sortedWorkers = (workersWithData as WorkerData[]).sort((a: WorkerData, b: WorkerData) => {
                      const aHasHVST = Object.values(a.taskGroups as any).some((g: any) => g.task?.task_type_moniker === 'HVST')
                      const bHasHVST = Object.values(b.taskGroups as any).some((g: any) => g.task?.task_type_moniker === 'HVST')

                      if (aHasHVST && !bHasHVST) return -1
                      if (!aHasHVST && bHasHVST) return 1

                      if (aHasHVST && bHasHVST) {
                        return b.totalHVSTBoxes - a.totalHVSTBoxes
                      }

                      return 0
                    })

                    return (sortedWorkers as WorkerData[]).map((data: WorkerData) => {
                      // Check if worker has only HVST tasks
                      const hasHVSTOnly = Object.values(data.taskGroups as any).every((g: any) => g.task?.task_type_moniker === 'HVST')
                      
                      if (hasHVSTOnly) {
                        // For HVST-only workers, show single line with percentage
                        const workerPercentage = teamTotalBoxes > 0 ? Math.round((data.totalHVSTBoxes / teamTotalBoxes) * 100) : 0
                        return (
                          <div key={data.worker.id} className="flex items-center justify-between px-2 py-1 bg-gray-50 rounded text-sm">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  const hvstTaskId = Object.entries(data.taskGroups).find(([_, g]) => g.task?.task_type_moniker === 'HVST')?.[0]
                                  if (hvstTaskId) {
                                    handleOpenSummaryModal(
                                      data.worker.id,
                                      `${data.worker.firstName} ${data.worker.lastName}`,
                                      hvstTaskId,
                                      'Cosecha'
                                    )
                                  }
                                }}
                                className="w-5 h-5 rounded-md flex items-center justify-center text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors flex-shrink-0"
                                title="Show all records"
                              >
                                ℹ
                              </button>
                              <span className="font-medium text-gray-900">
                                {data.worker.firstName} {data.worker.lastName}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-right">
                              <span className="font-semibold text-gray-900">
                                {formatNumber(data.totalHVSTBoxes)}
                              </span>
                              <span className="text-xs text-gray-500">
                                ({workerPercentage}%)
                              </span>
                            </div>
                          </div>
                        )
                      } else {
                        // For non-HVST workers, show full detail (multi-line)
                        const completedCount = Object.values(data.taskGroups).reduce((sum, g) => sum + g.completed, 0)
                        const totalCount = Object.values(data.taskGroups).reduce((sum, g) => sum + g.total, 0)
                        const percentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

                        return (
                          <div key={data.worker.id} className="border border-gray-200 rounded p-2 bg-gray-50 mb-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium text-gray-900 text-sm">
                                {data.worker.firstName} {data.worker.lastName}
                              </p>
                            </div>
                            <div className="space-y-1 ml-2 text-xs">
                              {Object.entries(data.taskGroups).map(([taskId, group]) => {
                                const g: any = group
                                return (
                                  <div key={taskId} className="flex justify-between text-gray-700">
                                    <span>{g.task?.task_type}:</span>
                                    <span className="font-semibold">
                                      {g.completed}/{g.total} ({Math.round((g.completed / g.total) * 100)}%)
                                    </span>
                                  </div>
                                )
                              })}
                              <div className="flex justify-between text-gray-900 font-semibold mt-1 pt-1 border-t border-gray-300">
                                <span>Total:</span>
                                <span>{completedCount}/{totalCount} ({percentage}%)</span>
                              </div>
                            </div>
                          </div>
                        )
                      }
                    })
                  })()}
                </div>
              </div>
            </div>
          ) : (
            // Grid View
            <div className="flex flex-col gap-6 py-6 pb-32">
          {/* Task Filter - Horizontal Scrollable Buttons */}
          {tasks.length > 0 && (
            <div className="overflow-x-auto -mx-4 px-4">
              <div className="flex gap-3 flex-nowrap">
                {tasks.map((task: TaskType) => {
                  const taskColor = getTaskColor(task.id)
                  const isSelected = selectedTasks.includes(task.id)
                  
                  return (
                    <button
                      key={task.id}
                      onClick={() => toggleTask(task.id)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex-shrink-0 whitespace-nowrap ${
                        isSelected
                          ? 'text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      style={isSelected ? { backgroundColor: taskColor } : {}}
                    >
                      {task.task_type}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Zone Grid Table - All Columns from Data */}
          {grid.length > 0 ? (
            <div className="overflow-x-auto -mx-4 px-4">
              <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
                <tbody>
                  {rowIndices.map((rowIdx) => (
                    <tr key={rowIdx} className="h-auto">
                      {grid.map((column, colIdx) => {
                        const zone = column[rowIdx]
                        const workersInZone = zone ? (workersByZone[zone.id] || []) : []

                        return (
                          <td
                            key={`${colIdx}-${rowIdx}`}
                            className="border border-gray-300 px-2 py-1 bg-white relative align-top min-h-[48px]"
                          >
                            {zone ? (
                              <>
                                {/* Zone Moniker - Background Text */}
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20">
                                  <span className="text-2xl font-bold text-gray-400">{zone.zone_moniker}</span>
                                </div>

                                {/* Worker Initials Grid */}
                                <div className="relative py-0">
                                  <div
                                    className={`flex flex-wrap items-center gap-1 leading-none min-h-[28px] ${
                                      workersInZone.length === 0 ? 'justify-center text-gray-300 text-[10px]' : ''
                                    }`}
                                  >
                                    {workersInZone.length > 0 ? (
                                      workersInZone.map(({ workerId, taskTypeId, locationCount, completedLocationCount }, idx) => {
                                        const worker = workersMap[workerId]
                                        if (!worker) return null

                                        const bgColor = getTaskColor(taskTypeId)
                                        const initials = `${worker.first_name?.[0]}${worker.last_name?.[0]}`.toUpperCase()
                                        const hasLocationData = locationCount > 0
                                        const hasMixedCompletion = hasLocationData && completedLocationCount > 0 && completedLocationCount < locationCount
                                        const showCompletionCount = hasMixedCompletion

                                        return (
                                          <div
                                            key={`${workerId}-${idx}`}
                                            className="flex items-center gap-1"
                                          >
                                            <div className="relative">
                                              <button
                                                onClick={() => setZoneDetailsView({
                                                  workerId,
                                                  taskTypeId,
                                                  zoneId: zone.id,
                                                  zoneName: zone.zone_moniker
                                                })}
                                                className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold text-white shadow-sm leading-none hover:opacity-80 hover:scale-110 transition-all cursor-pointer"
                                                style={{ backgroundColor: bgColor }}
                                                title={`${worker.first_name} ${worker.last_name}`}
                                              >
                                                {initials}
                                              </button>
                                              {/* Checkmark overlay for completed or value-logged assignments */}
                                              {(state.workerAssignments.some(a =>
                                                a.workerId === workerId &&
                                                a.taskTypeId === taskTypeId &&
                                                a.zoneId === zone.id &&
                                                !a.softDeleted &&
                                                a.completed
                                              ) || (isValueTypeTask(taskTypeId) && (getAssignmentZoneTotalValue(workerId, taskTypeId, zone.id) > 0 || getZoneLevelTotal(workerId, taskTypeId, zone.id) > 0))) && (
                                                <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-0.5 flex items-center justify-center">
                                                  <Check size={10} className="text-white" strokeWidth={3} />
                                                </div>
                                              )}
                                            </div>
                                            {showCompletionCount && (
                                                <span className="text-[9px] text-gray-600 font-medium leading-none inline-flex items-center">
                                                  ({completedLocationCount}/{locationCount})
                                                </span>
                                            )}
                                            {isValueTypeTask(taskTypeId) && (
                                              <>
                                                {getAssignmentZoneTotalValue(workerId, taskTypeId, zone.id) > 0 && (
                                                  <span className="text-[9px] text-green-600 font-semibold leading-none">
                                                    ({formatNumber(getAssignmentZoneTotalValue(workerId, taskTypeId, zone.id))})
                                                  </span>
                                                )}
                                                {getZoneLevelTotal(workerId, taskTypeId, zone.id) > 0 && (
                                                  <span className="text-[9px] text-green-600 font-semibold leading-none">
                                                    ({formatNumber(getZoneLevelTotal(workerId, taskTypeId, zone.id))})
                                                  </span>
                                                )}
                                              </>
                                            )}
                                          </div>
                                        )
                                      })
                                    ) : (
                                      <span className="w-full text-center">
                                        —
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="h-full" />
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Fallback title="No zones configured for this greenhouse" />
          )}
          </div>
            )}
        </Container>
      </div>

      {/* Record Value Modal */}
      {recordModalState && (
        <RecordValueModal
          isOpen={recordModalState.isOpen}
          title="# of Boxes"
          onSave={handleSaveRecordedValue}
          onCancel={() => setRecordModalState(null)}
        />
      )}

      {/* Summary Modal */}
      {summaryModalState && (
        <WorkerValueSummaryModal
          isOpen={summaryModalState.isOpen}
          workerName={summaryModalState.workerName}
          taskName={summaryModalState.taskName}
          summaryData={getWorkerValueSummary(summaryModalState.workerId, summaryModalState.taskTypeId)}
          onClose={() => setSummaryModalState(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmLogId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xs mx-4">
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Record?</h3>
              <p className="text-sm text-gray-600 mb-6">This record will be marked as deleted but kept for audit purposes.</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setDeleteConfirmLogId(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    handleDeleteRecordedValue(deleteConfirmLogId)
                    setDeleteConfirmLogId(null)
                  }}
                  className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AnalyticsPage
